import { STORE_SCHEDULE_QUERY } from '@/store/actions/schedule';
import dateMixin from './dateMixin';

const allEmployeesValue = 'all';
const categoryCabinets = 'cabinets';
const categoryEmployees = 'employees';
const periodDay = 'day';
const periodMonth = 'month';
const periodWeek = 'week';

export default {
  allEmployeesValue,
  categoryCabinets,
  categoryEmployees,
  periodDay,
  periodMonth,
  periodWeek,
  computed: {
    /**
     * @description Общий обзор сотрудников.
     * @type {boolean}
     */
    isAllEmployees({ selectedEmployeeId, $options }) {
      return selectedEmployeeId === $options.allEmployeesValue;
    },
    /**
     * @description Дата в формате ISO.
     * @type {string}
     */
    isoDate() {
      return this.formatDateToISO(this.date);
    },
    /**
     * @description Запрашиваемый период.
     * @returns {Date | {form:Date, to:Date}}
     */
    range({ date, period, $options }) {
      if (period === $options.periodDay) {
        return date;
      }
      if (period === $options.periodWeek) {
        const from = new Date(date.toISOString());
        const to = new Date(date.toISOString());
        const fromDay = date.getDate() - date.getDay() + 1;
        const toDay = fromDay + 6;
        from.setDate(fromDay);
        to.setDate(toDay);
        return { from, to };
      }
      const from = new Date(date.getFullYear(), date.getMonth(), 1);
      const to = new Date(
        date.getFullYear(),
        date.getMonth(),
        this.getLastDayOfMonth(date.getFullYear(), date.getMonth()),
      );
      return { from, to };
    },
    viewQuery() {
      const { fromEmployees } = this.$route.query;
      return {
        cabinetIds: this.selectedCabinetIds,
        category: this.category,
        day: this.isoDate,
        employeeId: this.selectedEmployeeId ?? 'all',
        ...(fromEmployees && { fromEmployees }),
        period: this.period,
      };
    },
  },
  data() {
    return {
      category: this.initialCategory || this.$options.categoryEmployees,
      date: this.initialDate || new Date(),
      period: this.initialPeriod || this.$options.periodDay,
      selectedEmployeeId: this.initialEmployeeId || this.$options.allEmployeesValue,
    };
  },
  methods: {
    /**
     * @description Получить список работающих сотрудников.
     * @param {string} day    День.
     * @param {string} period Период.
     */
    async fetchWorkingEmployees(day, period) {
      const workingEmployees = await this
        .$appointmentScheduleGateway
        .viewWorkingEmployeeList(day, period);
      return workingEmployees.fold([])((list) => list);
    },
    /**
     * @description Получить последний день месяца.
     * @param year
     * @param month
     * @returns {number}
     */
    getLastDayOfMonth(year, month) {
      const date = new Date(year, month + 1, 0);
      return date.getDate();
    },
    /**
     * @description Подготовить запрос.
     * @param {any} query
     * @returns {any}
     */
    prepareQuery(query) {
      const result = {
        day: query.day,
      };
      const { step } = this.$route.query;
      if (step) {
        result.step = step;
      }
      return result;
    },
    /**
     * @description Подготовить параметры.
     * @returns {any}
     */
    prepareParams() {
      return {
        ...this.$route.params,
        initialCabinets: this.cabinetList,
        initialEmployees: this.employeeList,
        initialOrders: this.ordersByAggregators,
        prevCategory: this.initialCategory,
      };
    },
    /**
     * @description Открыть расписание записей на прием.
     * @param {any} query
     * @returns {void}
     */
    openAppointmentSchedule(query) {
      if (query.category === this.$options.categoryEmployees) {
        if (query.period === this.$options.periodMonth) {
          this.openEmployeeScheduleMonth(query);
        } else if (
          (query.employeeId !== this.$options.allEmployeesValue
          && query.fromEmployees !== this.$options.allEmployeesValue)
          || query.period === this.$options.periodWeek
        ) {
          this.openEmployeeSchedulePeriod(query);
        } else {
          this.openAllEmployeeScheduleDay(query);
        }
      } else if (query.category === this.$options.categoryCabinets) {
        if (query.period === this.$options.periodDay) {
          this.openCabinetsScheduleDay(query);
        } else if (query.period === this.$options.periodMonth) {
          this.openCabinetScheduleMonth(query);
        } else if (query.period === this.$options.periodWeek) {
          this.openCabinetSchedulePeriod(query);
        }
      }
    },
    /**
     * @description Открыть расписание по кабинетам на день.
     * @param {any} query
     * @returns {void}
     */
    async openCabinetsScheduleDay(query) {
      const routeName = 'ClinicPanelAppointmentScheduleCabinets';
      const queryParams = this.prepareQuery(query);
      const params = this.prepareParams();
      const queryCategoryNotChanged = this.initialCategory === query.category;
      const queryPeriodNotChanged = this.initialPeriod === query.period;
      if (
        query.cabinetIds.length > 0
        && queryCategoryNotChanged
        && queryPeriodNotChanged
      ) {
        queryParams.cabinetIds = query.cabinetIds;
      } else {
        queryParams.cabinetIds = this.cabinetList.map((cabinet) => cabinet.id);
        params.initialCabinets = this.cabinetList;
      }
      this.storeSheduleQueryParams(routeName, params, queryParams, true);
      this.$router.push({ name: routeName, params, query: queryParams });
    },
    /**
     * @description Открыть расписание по кабинету на неделю.
     * @param {any} query
     * @returns {void}
     */
    async openCabinetSchedulePeriod(query) {
      const routeName = 'ClinicPanelAppointmentScheduleCabinetPeriod';
      const queryParams = this.prepareQuery(query);
      queryParams.period = query.period;
      const params = this.prepareParams();
      if (query.cabinetIds.length > 0) {
        [params.id] = query.cabinetIds;
      } else {
        params.id = this.cabinetList[0].id;
        params.initialCabinets = this.cabinetList;
      }
      this.storeSheduleQueryParams(routeName, params, queryParams, true);
      this.$router.push({ name: routeName, params, query: queryParams });
    },
    /**
     * @description Открыть расписание по кабинету на месяц.
     * @param {any} query
     * @returns {void}
     */
    async openCabinetScheduleMonth(query) {
      const routeName = 'ClinicPanelAppointmentScheduleCabinetMonth';
      const params = this.prepareParams();
      if (query.cabinetIds.length > 0) {
        [params.id] = query.cabinetIds;
      } else {
        params.id = this.cabinetList[0].id;
        params.initialCabinets = this.cabinetList;
      }
      const date = new Date(query.day);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      params.month = `${year}-${month}`;
      params.day = query.day;
      this.storeSheduleQueryParams(routeName, params, {}, true);
      this.$router.push({ name: routeName, params });
    },
    /**
     * @description Открыть расписание всех сотрудников на день.
     * @param {any} query
     * @returns {void}
     */
    openAllEmployeeScheduleDay(query) {
      const routeName = 'ClinicPanelAppointmentScheduleEmployees';
      const queryParams = this.prepareQuery(query);
      const params = this.prepareParams();
      this.storeSheduleQueryParams(routeName, {}, queryParams, false);
      this.$router.push({ name: routeName, params, query: queryParams });
    },
    /**
     * @description Открыть расписание сотрудника на период.
     * @param {any} query
     * @returns {void}
     */
    async openEmployeeSchedulePeriod(query) {
      const { day, employeeId, period } = query;
      const byAllEmployees = employeeId === this.$options.allEmployeesValue;
      const params = this.prepareParams();
      if (byAllEmployees) {
        const initialEmployees = await this.fetchWorkingEmployees(day, period);
        params.initialEmployees = initialEmployees;
        if (initialEmployees.length !== 0) {
          params.id = initialEmployees[0].id;
        } else {
          params.id = this.$options.allEmployeesValue;
        }
      } else {
        params.id = employeeId;
      }
      const routeName = 'ClinicPanelAppointmentScheduleEmployeePeriod';
      const queryParams = { ...this.prepareQuery(query), period };
      this.storeSheduleQueryParams(routeName, params, queryParams, true);
      this.$router.push({
        name: routeName,
        params,
        query: queryParams,
      });
    },
    /**
     * @description Открыть расписание сотрудника на месяц.
     * @param {any} query
     * @returns {void}
     */
    async openEmployeeScheduleMonth(query) {
      const { day, employeeId, period } = query;
      const routeName = 'ClinicPanelAppointmentScheduleEmployeeMonth';
      const date = new Date(day);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const yearAndMonth = `${year}-${month}`;
      const byAllEmployees = employeeId === this.$options.allEmployeesValue;
      const params = this.prepareParams();
      if (byAllEmployees) {
        const initialEmployees = await this.fetchWorkingEmployees(day, period);
        params.initialEmployees = initialEmployees;
        if (initialEmployees.length !== 0) {
          params.id = initialEmployees[0].id;
        } else {
          params.id = this.$options.allEmployeesValue;
        }
      } else {
        params.id = employeeId;
      }
      params.month = yearAndMonth;
      params.day = day;
      this.storeSheduleQueryParams(
        routeName,
        params,
        { ...(byAllEmployees && { fromEmployees: this.$options.allEmployeesValue }) },
        true,
      );
      this.$router.push({
        name: routeName,
        params,
        query: { ...(byAllEmployees && { fromEmployees: this.$options.allEmployeesValue }) },
      });
    },
    storeSheduleQueryParams(name, params, query, needRedirect) {
      this.$store.commit(
        STORE_SCHEDULE_QUERY,
        {
          name,
          params,
          query,
          needRedirect,
        },
      );
    },
  },
  mixins: [dateMixin],
  props: {
    /**
     * @description Список кабинетов.
     * @type {({color: string, id: string, name: string}[])}
     */
    cabinetList: { type: Array, default: () => [] },
    /**
      * @description Список сотрудников.
      * @type {any[]}
      */
    employeeList: { type: Array, default: () => [] },
    /**
     * @description Категория.
     * @type {string}
     */
    initialCategory: String,
    /**
      * @description Дата.
      * @type {Date}
      */
    initialDate: Date,
    /**
      * @description Идентификатор выбранного сотрудника.
      * @type {number}
      */
    initialEmployeeId: { type: [String, Number] },
    /**
      * @description Период.
      * @type {string}
      */
    initialPeriod: String,
    /**
     * @description Предыдущая категория.
     * @type {string}
     */
    prevCategory: { type: String },
  },
  watch: {
    initialDate(value, oldValue) {
      if (this.period === this.$options.periodMonth) {
        if (value.getYear() !== oldValue.getYear() || value.getMonth() !== oldValue.getMonth()) {
          this.date = value;
        }
      } else if (this.isoDate !== this.formatDateToISO(value)) {
        this.date = value;
      }
    },
  },
};
